import { useEffect, useRef } from "react";
import { useWindowDimensions } from "react-native";
import { newRidgeState } from "react-ridge-state";

const scrollState = newRidgeState<number>(0);
const scrollDirectionState = newRidgeState<string>("up");
const SCROLL_THRESHOLD = 50;
const lastScrollPositionState = newRidgeState<number>(0);

export function useScroll() {
	const [scroll, setScroll] = scrollState.use();
	const { height: windowHeight } = useWindowDimensions();
	const [scrollDirection, setScrollDirection] = scrollDirectionState.use();
	const [lastScrollPosition, setLastScrollPosition] = lastScrollPositionState.use();
	const scrollTimer = useRef<NodeJS.Timeout | null>(null);

	const onScroll = (e: any) => {
		const currentScrollY = e.nativeEvent.contentOffset.y;
		if (currentScrollY === lastScrollPosition) return;

		// Clear any existing timer
		if (scrollTimer.current) {
			clearTimeout(scrollTimer.current);
		}

		// Calculate the difference from the last significant scroll position
		const scrollDifference = currentScrollY - lastScrollPosition;

		// Only update direction if threshold is met
		if (Math.abs(scrollDifference) >= SCROLL_THRESHOLD) {
			const newDirection = scrollDifference > 0 ? "down" : "up";

			// Update the significant scroll position
			setLastScrollPosition(currentScrollY);

			if (newDirection !== scrollDirection) {
				setScrollDirection(newDirection);
			}

			// Start timer to reset to "up" if no significant scroll for a while
			scrollTimer.current = setTimeout(() => {
				setScrollDirection("up");
			}, 1000);
		}

		// Always update current scroll position
		setScroll(currentScrollY);
	};

	useEffect(() => {
		setScrollDirection("up");

		// Cleanup timer on component unmount
		return () => {
			if (scrollTimer.current) {
				clearTimeout(scrollTimer.current);
			}
		};
	}, [windowHeight]);

	return { scroll, scrollDirection, onScroll };
}
